import { ChangeEvent, FocusEvent, KeyboardEvent, useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { Colors } from '../constants/colors'
import { cardPadding } from '../constants/layout'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import S2 from './typography/S2'

interface Props {
  className?: string
  id: string
  value: string
  error?: string
  placeholder?: string
  iconName?: string | IconDefinition
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  onEnterKeyOrBlur?: (value: string) => void
  valid?: boolean
  disabled?: boolean
  focused?: boolean
  label?: string
  numeric?: boolean
}

const InputField = ({
  className,
  id = '',
  value = '',
  error = '',
  placeholder = 'Placeholder',
  iconName,
  onChange = () => '',
  onEnterKeyOrBlur = () => '',
  valid = true,
  disabled = false,
  focused = false,
  label,
  numeric = false,
}: Props) => {
  const styles = useStyles()
  const [inputFieldValue, setInputFieldValue] = useState(value)

  useEffect(() => {
    if (value !== inputFieldValue) {
      setInputFieldValue(value)
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputFieldValue(event.target.value)
    onChange(event)
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    onEnterKeyOrBlur(event.target.value)
 }

  const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onEnterKeyOrBlur(inputFieldValue)
    }
  }

  const renderIcon = () => {
    return iconName && (
      <FontAwesomeIcon
        className={styles.icon}
        icon={iconName as IconDefinition}
        color={Colors.darkDefault}
      />
    )
  }

  const renderLabel = () => {
    return label && (
      <S2 className={styles.label}>{label}</S2>
    )
  }

  const renderError = () => {
    return error && (
      <span className={styles.error}>{error}</span>
    )
  }

  const paddingLeft = iconName ? cardPadding * 2.5 : cardPadding

  return (
    <>
      {renderLabel()}
      <div className={`${styles.container} ${className}`} style={{borderColor: valid ? 'transparent' : Colors.rubel_700, borderWidth: valid ? 0 : 1}}>
        {renderIcon()}
        <input
          id={id}
          className={styles.input}
          style={{ paddingLeft }}
          onBlur={handleBlur}
          onKeyUp={handleKeyUp}
          onChange={handleChange}
          value={inputFieldValue}
          type={numeric ? 'number' : 'text'}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
      {renderError()}
    </>
  )
}

export default InputField

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    borderRadius: 5,
    borderStyle: 'solid',
    backgroundColor: Colors.darkGray2,
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    }
  },
  input: {
    width: '100%',
    border: 'none',
    borderRadius: 5,
    backgroundColor: 'transparent',
    fontFamily: 'Aestetico-Regular',
    fontSize: 16,
    padding: {
      top: cardPadding * 0.5,
      bottom: cardPadding * 0.5,
      right: cardPadding * 2.5,
      // left: cardPadding * 2.5,
    },
    '&:focus': {
      border: 'none',
      backgroundColor: Colors.darkGray3,
      outline: 'none',
    },
    '&:active': {
      backgroundColor: Colors.darkGray3,
    },
  },
  icon: {
    position: 'absolute',
    left: cardPadding,
  },
  label: {
    marginBottom: 8,
    marginTop: 8
  },
  error: {
    marginTop: 4,
    fontSize: 12,
    color: Colors.rubel_700,
    display: 'block',
  },
})
